/**
 * Calendar utility functions for downloading and handling .ics files
 */

/**
 * Extracts filename from Content-Disposition header
 * @param headers - Response headers object
 * @returns Extracted filename or null if not found
 */
export const extractFilenameFromHeaders = (headers: any): string | null => {
    if (!headers) {
        return null;
    }

    const contentDisposition =
        headers["content-disposition"] || headers["Content-Disposition"];

    if (!contentDisposition) {
        return null;
    }

    // Try to match filename="..." or filename*=UTF-8''...
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);

    if (filenameMatch && filenameMatch[1]) {
        let filename = filenameMatch[1].replace(/['"]/g, "");

        // Handle UTF-8 encoding: filename*=UTF-8''encoded-filename
        if (filename.startsWith("UTF-8''")) {
            filename = decodeURIComponent(filename.substring(7));
        }

        return filename;
    }

    return null;
};

/**
 * Generates a default filename for calendar file when header extraction fails
 * @param reservationId - The reservation ID
 * @returns Generated filename
 */
export const generateDefaultFilename = (reservationId: number): string => {
    return `Planny_Reservation_${reservationId}.ics`;
};

/**
 * Triggers a file download in the browser using a Blob
 * @param blob - The Blob containing file data
 * @param filename - The filename to use for download
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
    // Create object URL from blob
    const url = URL.createObjectURL(blob);

    // Create temporary anchor element
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";

    // Append to body, trigger click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up object URL after a short delay to ensure download starts
    setTimeout(() => {
        URL.revokeObjectURL(url);
    }, 100);
};

/**
 * Downloads a calendar (.ics) file from byte array
 * @param bytes - ArrayBuffer containing the .ics file data
 * @param filename - The filename to use for download
 */
export const downloadCalendarFile = (bytes: ArrayBuffer, filename: string): void => {
    // Create blob with proper MIME type for calendar files
    const blob = new Blob([bytes], { type: "text/calendar; charset=utf-8" });
    downloadBlob(blob, filename);
};
